import { biLogger, fedopsLogger } from '#packages/util';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { componentAddedToStage } from '@wix/bi-logger-editor/v2';

import type {
  AddPanelComponentAddedToStageByDrag,
  AddPanelComponentAddedToStageByClick,
} from '#packages/components';
import type { EditorAPI } from '#packages/editorAPI';
import type { DraggableItem, OnDropCallback } from '#packages/addPanelInfra';
import type {
  Point,
  CompRef,
  CompStructure,
  SerializedCompStructure,
} from 'types/documentServices';
import type { PresetsValue } from '@wix/document-services-types';

const getScopedPresets = (
  scopedPresets: SerializedCompStructure['scopedPresets'] = {},
  mobileVariantId: string,
) => {
  if (!scopedPresets[mobileVariantId] && scopedPresets.mobile) {
    return {
      // TODO: need to remove all 'mobile' scopedPresets in CMS, since now serialize works with the real mobile variant.
      [mobileVariantId]: scopedPresets.mobile,
    };
  }
  return scopedPresets;
};

const getSemanticVersionForApp = async (
  editorAPI: EditorAPI,
  appDefinitionId: string,
) => {
  const marketData =
    await editorAPI.dsRead.tpa.appMarket.getDataAsync(appDefinitionId);

  const { publishedVersion, latestVersion } = marketData.appFields.appService;
  const version = publishedVersion || latestVersion;
  return `^${version.split('.')[0]}.0.0`;
};

const installAndAddBlocksWidget = async (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  compStructure: CompStructure,
  containerRef: CompRef,
  type: (
    | AddPanelComponentAddedToStageByDrag
    | AddPanelComponentAddedToStageByClick
  )['type'],
  coords: Point,
  biOrigin: string,
  presetId: string,
  categoryId: string,
  sectionTitle: string,
): Promise<CompRef> => {
  fedopsLogger.interactionStarted(
    fedopsLogger.INTERACTIONS.ADD_PANEL.ADD_BLOCKS_WIDGET,
  );

  const shouldInstallLatest = window.queryUtil.isParameterTrue(
    'blocksInstallLatestVersionFromAddPanel',
  );

  await editorAPI.tpa.installAppIfNeeded(appDefinitionId, {
    resolveBeforeSave: true,
    platformProvisionOnly: true,
    appVersion: shouldInstallLatest
      ? 'latest'
      : await getSemanticVersionForApp(editorAPI, appDefinitionId),
  });

  const { data, layout, presets, scopedPresets } =
    compStructure as SerializedCompStructure;
  const { widgetId } = data;
  const mobileVariant = editorAPI.mobile.getMobileVariant();
  return new Promise((resolve, reject) => {
    editorAPI.dsActions.appStudioWidgets.addWidget(appDefinitionId, widgetId, {
      presets: presets as unknown as PresetsValue,
      containerRef,
      scopedPresets: getScopedPresets(scopedPresets, mobileVariant.id),
      installationType: 'closed',
      layout: { ...layout, ...coords },
      onSuccess: async (newWidgetRef: CompRef) => {
        await editorAPI.components.hooks.componentAddedToStage.fire({
          type,
          origin: 'addPanel',
          compRef: newWidgetRef,
          mousePosition: coords,
        });
        editorAPI.selection.selectComponentByCompRef(newWidgetRef);
        resolve(newWidgetRef);
        fedopsLogger.interactionEnded(
          fedopsLogger.INTERACTIONS.ADD_PANEL.ADD_BLOCKS_WIDGET,
        );
        biLogger.report(
          componentAddedToStage({
            ...editorAPI.bi.getComponentsBIParams([newWidgetRef])[0],
            origin: biOrigin,
            category: categoryId,
            section: sectionTitle,
            preset_id: presetId,
            adding_method: type,
            page_id: editorAPI.pages.getFocusedPageId(),
            target_component: editorAPI.components.getType(containerRef),
            target_component_id: containerRef.id,
          }),
        );
      },
      onError: (e) => {
        ErrorReporter.captureException(e, {
          tags: { failedToAddAppStudioWidget: true },
        });
        reject(e);
      },
    });
  });
};

interface AddBlocksWidgetParams {
  appDefinitionId: string;
  compStructure: CompStructure;
  biOrigin: string;
  presetId: string;
  categoryId: string;
  sectionTitle: string;
}

export const addBlocksWidgetOnClick = async (
  editorAPI: EditorAPI,
  {
    biOrigin,
    presetId,
    categoryId,
    sectionTitle,
    compStructure,
    appDefinitionId,
  }: AddBlocksWidgetParams,
) => {
  const containerToAddTo =
    editorAPI.addPanelInfra.addPanelUtils.getContainerToAddTo(
      editorAPI,
      compStructure,
      null,
    );
  const { width: parentWidth } =
    editorAPI.components.layout.get_size(containerToAddTo);
  const coords = {
    y: 0,
    x: (parentWidth - compStructure.layout.width) / 2,
  };
  await installAndAddBlocksWidget(
    editorAPI,
    appDefinitionId,
    compStructure,
    containerToAddTo,
    'click',
    coords,
    biOrigin,
    presetId,
    categoryId,
    sectionTitle,
  );
};

export const addBlocksWidgetOnDrop = (
  editorAPI: EditorAPI,
  {
    biOrigin,
    presetId,
    categoryId,
    sectionTitle,
    compStructure,
    appDefinitionId,
  }: AddBlocksWidgetParams,
): OnDropCallback => {
  return async (
    coords: Point,
    itemData: DraggableItem,
    containerRef: CompRef,
  ) => {
    await installAndAddBlocksWidget(
      editorAPI,
      appDefinitionId,
      compStructure,
      containerRef,
      'drag',
      coords,
      biOrigin,
      presetId,
      categoryId,
      sectionTitle,
    );
  };
};
